import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Space,
  Typography,
  DatePicker,
  Radio,
  Checkbox,
  InputNumber,
  Row,
  Col,
  Button,
  message,
} from "antd";
import { SendOutlined, CloseOutlined } from "@ant-design/icons";
import useToken from "../Authentication/useToken";
import { useParams, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import Constants from "../Utils/Constants";

export default function FollowUpCallPACE() {
  const { t } = useTranslation("FollowUpCall");
  const { token } = useToken();
  const { patientId } = useParams();
  const location = useLocation();
  const eventDate = location.state?.eventDate;
  const [form] = Form.useForm();
  const [completedTypes, setCompletedTypes] = useState([]);

  useEffect(() => {
    if (token && patientId) {
      fetch(
        `${Constants.SERVER_URL}/follow-up/follow-up-calls-pace/patient/${patientId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
        .then((res) => res.json())
        .then((data) => {
          if (Array.isArray(data)) {
            setCompletedTypes(data.map((f) => f.evaluationType));
          }
        })
        .catch((err) =>
          console.error("Erreur chargement follow-ups:", err)
        );
    }
  }, [token, patientId]);
  useEffect(() => {
    if (!eventDate) return;

    const parsedEventDate = dayjs(eventDate);
    if (parsedEventDate.isValid()) {
      form.setFieldsValue({ "current-date": parsedEventDate });
    }
  }, [eventDate, form]);

  const onFinish = async (values) => {
    const toCamelCase = (key) => key.replace(/-([a-z0-9])/g, (_, c) => c.toUpperCase());

    const data = { patientId };
    for (const [key, value] of Object.entries(values)) {
      let camelKey = toCamelCase(key);
      if (camelKey === "hospitalDaySurgery") camelKey = "hasBeenToHospital";

      if (value === "yes") data[camelKey] = true;
      else if (value === "no") data[camelKey] = false;
      else if (key === "current-date" && value) data[camelKey] = value.format("YYYY-MM-DD");
      else if (camelKey === "selfHealthRating" || camelKey === "programDifficultyLevel")
        data[camelKey] = parseInt(value, 10);
      else data[camelKey] = value;
    }

    try {
  const response = await fetch(
    `${Constants.SERVER_URL}/follow-up/create-follow-up-call-pace`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }
  );
      if (response.ok) {
        message.success(t("success_followup_created"));
        window.location.href = "/evaluations";
      } else {
        message.error(t("error_followup_creation"));
      }
    } catch (error) {
      console.error("Erreur:", error);
      message.error(t("error_followup_creation"));
    }
  };

  // Additional question toggles
  const [programDifficultyLevel, setProgramDifficultyLevel] = useState(null);
  const [programPainLevel, setProgramPainLevel] = useState(null);
  const [sessionSafetyFeel, setSessionSafetyFeel] = useState(null);
  const [hasWalkedPastWeeks, setHasWalkedPastWeeks] = useState(null);
  const [hasFallenPastWeeks, setHasFallenPastWeeks] = useState(null);
  const [isMotivatedByAP, setIsMotivatedByAP] = useState(null);
  const [otherActivityIntegration, setOtherActivityIntegration] = useState(null);
  const [anotherActivityIntegration, setAnotherActivityIntegration] = useState(null);
  const [yetAnotherActivityIntegration, setYetAnotherActivityIntegration] = useState(null);
  const [hasSeenDoctor, setHasSeenDoctor] = useState(null);
  const [hasBeenToHospital, setHasBeenToHospital] = useState(null);
  const [hasConsultedSpecialist, setHasConsultedSpecialist] = useState(null);

  // Additional question inputs
  const inputTextArea = () => {
    return (<Input.TextArea
      style={{minHeight: "110px"}}
    />)
  }

  const inputNumber = (label, inputName, min, extra="", suffix="") => {
    return(
      <Form.Item
        label={t(label)}
        name={inputName}
        extra={t(extra)}
      >
        <InputNumber
          min={min}
          suffix={t(suffix)}
          style={{width: "130px"}}
          placeholder={String(min)}
        />
      </Form.Item>
    )
  }

  const inputYesOrNo = (label, set, val, inputName) => {
    return(<Form.Item
      label={t(label)}
      name={inputName}
      rules={[{required: true, message: t("error_form_required_field")}]}
    >
      <Radio.Group
        onChange={(e) => {set(e.target.value)}}
      >
        <Space direction="vertical">
          <Radio value="yes">{t("label_yes")}</Radio>
          <Radio value="no">{t("label_no")}</Radio>
        </Space>
      </Radio.Group>
    </Form.Item>)
  }

  const inputExerciseDifficulty = (number) => {
    let inputList = [];
    for (let i = 0; i < number; i++) {
      let position = i + 1;
      inputList.push(
        <Form.Item
          key={`exercise-difficulty-${position}`}
          label={`${t("label_exercise")} ${position}`}
          name={`exercise-${position}-difficulty`}
          layout="horizontal"
        >
          <Radio.Group
          >
            <Radio value="doesnt-do">{t("label_doesnt_do_exercise")}</Radio>
            <Radio value="level-1">{t("label_difficulty_1")}</Radio>
            <Radio value="level-2">{t("label_difficulty_2")}</Radio>
            <Radio value="level-3">{t("label_difficulty_3")}</Radio>
          </Radio.Group>
        </Form.Item>
      )
    }
    return (inputList)
  }

  const inputsWeeklyFrequency = () => {
    return ([
      <Radio key="once" value="once">{t("label_once_a_week")}</Radio>,
      <Radio key="twice" value="twice">{t("label_twice_a_week")}</Radio>,
      <Radio key="three" value="three">{t("label_three_a_week")}</Radio>,
      <Radio key="four" value="four">{t("label_four_a_week")}</Radio>,
      <Radio key="five" value="five">{t("label_five_a_week")}</Radio>,
      <Radio key="six" value="six">{t("label_six_a_week")}</Radio>,
      <Radio key="seven" value="seven">{t("label_seven_a_week")}</Radio>
    ])
  }

  const inputsPracticeWay = (label, inputName) => {
    return (
      <Form.Item
        label={t(label)}
        name={inputName}
        rules={[{required: true, message: t("error_form_required_field")}]}
      >
        <Radio.Group
        >
          <Space direction="vertical">
            <Radio value="alone">{t("label_alone")}</Radio>
            <Radio value="with-caregiver">{t("label_with_caregiver")}</Radio>
            <Radio value="caregiver-supervision">{t("label_supervision_caregiver")}</Radio>
            <Radio value="with-friends">{t("label_with_friends")}</Radio>
          </Space>
        </Radio.Group>
      </Form.Item>
    )
  }

  const inputsOtherActivity = (count) => {
    return (
      <div id={`additional-activity-${count}`}>
        <Form.Item
          label={t("label_if_yes_what")}
          name={`additional-activity-${count}-name`}
        >
          <Input
            placeholder={t("placeholder_activity_name")}
          />
        </Form.Item>

        <Form.Item
          label={t("label_if_yes_what_frequency")}
          name={`additional-activity-${count}-frequency`}
        >
          <Radio.Group
          >
            <Space direction="vertical">
              {inputsWeeklyFrequency()}
            </Space>
          </Radio.Group>
        </Form.Item>

          {inputNumber(
            "label_if_yes_what_length",
            `additional-activity-${count}-length`,
            1,
            "hint_average_minutes",
            "input_suffix_minutes"
          )}

        <Form.Item
          label={t("label_if_yes_what_intensity")}
          name={`additional-activity-${count}-intensity`}
        >
          <Radio.Group
          >
            <Radio value="can-talk-easily">{t("label_can_talk_easily")}</Radio>
            <Radio value="difficulty-talking">{t("label_difficulty_talking")}</Radio>
            <Radio value="cant-talk">{t("label_cant_talk")}</Radio>
          </Radio.Group>
        </Form.Item>

        {count === 1 && (
          inputYesOrNo(
            "label_integrated_another_activity",
            setAnotherActivityIntegration,
            anotherActivityIntegration,
            "another-activity-integration"
          )
        )}
        {count === 2 && (
          inputYesOrNo(
            "label_is_there_other",
            setYetAnotherActivityIntegration,
            yetAnotherActivityIntegration,
            "yet-another-activity-integration"
          )
        )}
    </div>
  )}

  const inputsConsultation = (consult) => {
    return (
      <div id={`${consult}-additional`}>
        {inputNumber(
          "label_if_yes_how_much",
          `times-visited-${consult}-count`,
          1
        )}

        <Form.Item
          label={t("label_if_yes_reason")}
          name={`${consult}-visit-reasons`}
        >
          {inputTextArea()}
        </Form.Item>
      </div>
    )
  }

  return (
    <Row justify="center">
      <Col span={15}>
        <Form
          form={form}
          layout={"vertical"}
          onFinish={onFinish}
        >
          <Typography.Title level={1} style={{textAlign: "center"}}>
            {t("title_follow_up_pace")}
          </Typography.Title>

          {/* Patient information section */}
          <Typography.Title level={3}>
            {t("section_patient_information")}
          </Typography.Title>

          <p>{t("label_follow_up_record")} : {patientId}</p>

          <Form.Item
            label={t("label_date_of_day")}
            name="current-date"
            rules={[
              {required: true, message: t("error_form_required_field")}
            ]}
            >
            <DatePicker
              placeholder={t("placeholder_date_of_day")}
            />
          </Form.Item>

          <Form.Item
            label={t("label_kinesiologist_name")}
            name="kinesologist-name"
            rules={[{required: true, message: t("error_form_required_field")}]}
            >
            <Input
              placeholder={t("placeholder_kinesiologist_name")}
            />
          </Form.Item>

          <Form.Item
            label={t("label_evaluation_type")}
            name="evaluation-type"
            rules={[{required: true, message: t("error_form_required_field")}]}
            >
            <Radio.Group
            >
              <Radio value="s1" disabled={completedTypes.includes("s1")}>{t("label_type_s1")}</Radio>
              <Radio value="s3" disabled={completedTypes.includes("s3")}>{t("label_type_s3")}</Radio>
              <Radio value="s6" disabled={completedTypes.includes("s6")}>{t("label_type_s6")}</Radio>
              <Radio value="s9" disabled={completedTypes.includes("s9")}>{t("label_type_s9")}</Radio>
              <Radio value="s12" disabled={completedTypes.includes("s12")}>{t("label_type_s12")}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={t("label_one_ten_scale_health")}
            name="self-health-rating"
            rules={[{required: true, message: t("error_form_required_field")}]}
          >
            <Radio.Group
            >
              <Radio value="1">1</Radio>
              <Radio value="2">2</Radio>
              <Radio value="3">3</Radio>
              <Radio value="4">4</Radio>
              <Radio value="5">5</Radio>
              <Radio value="6">6</Radio>
              <Radio value="7">7</Radio>
              <Radio value="8">8</Radio>
              <Radio value="9">9</Radio>
              <Radio value="10">10</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={t("label_program_difficulty_level")}
            name="program-difficulty-level"
            rules={[{required: true, message: t("error_form_required_field")}]}
          >
            <Radio.Group
              onChange={(e) => {setProgramDifficultyLevel(e.target.value)}}
            >
              <Radio value="1">{t("label_level_I")}</Radio>
              <Radio value="2">{t("label_level_II")}</Radio>
              <Radio value="3">{t("label_level_III")}</Radio>
              <Radio value="4">{t("label_level_IV")}</Radio>
              <Radio value="5">{t("label_level_V")}</Radio>
            </Radio.Group>
          </Form.Item>
          
          <Form.Item
            label={t("label_average_prescribed_exercise")}
            name="average-prescribed-exercise-done"
            rules={[{required: true, message: t("error_form_required_field")}]}
          >
            <Radio.Group
            >
              <Space direction="vertical">
                <Radio value="never">{t("label_never")}</Radio>
                <Radio value="less-than-once">{t("label_less_than_once_a_week")}</Radio>
                {inputsWeeklyFrequency()}
              </Space>
            </Radio.Group>
          </Form.Item>

          {inputsPracticeWay(
            "label_prescription_practice_way",
            "exercise-prescription-practice-way"
          )}

          <Form.Item
            label={t("label_prescribed_exercice_opinion")}
            name="prescriped-exercise-opinion"
            rules={[{required: true, message: t("error_form_required_field")}]}
          >
            <Radio.Group
            >
              <Space direction="vertical">
                <Radio value="very-easy">{t("label_very_easy")}</Radio>
                <Radio value="easy">{t("label_easy")}</Radio>
                <Radio value="hard">{t("label_hard")}</Radio>
                <Radio value="very-hard">{t("label_very_hard")}</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>

          {/* Section : Difficulte exercices */}
          <Typography.Title level={3}>
            {t("section_exercise_difficulty")}
          </Typography.Title>

          {(programDifficultyLevel === "1" || programDifficultyLevel === "2") && inputExerciseDifficulty(2)}
          {(programDifficultyLevel === "3") && inputExerciseDifficulty(3)}
          {(programDifficultyLevel === "4" || programDifficultyLevel === "5") && inputExerciseDifficulty(4)}

          <Form.Item
            label={t("label_ap_satisfaction_level")}
            name="ap-satisfaction-level"
            rules={[{required: true, message: t("error_form_required_field")}]}
          >
            <Radio.Group
            >
              <Radio value="very-unsatisfied">{t("label_very_unsatisfied")}</Radio>
              <Radio value="unsatisfied">{t("label_unsatisfied")}</Radio>
              <Radio value="satisfied">{t("label_satisfied")}</Radio>
              <Radio value="very-satisfied">{t("label_very_satisfied")}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={t("label_program_pain_level")}
            name="program-pain-level"
            rules={[{required: true, message: t("error_form_required_field")}]}
          >
            <Radio.Group
              onChange={(e) => {setProgramPainLevel(e.target.value)}}
            >
              <Radio value="no-pain">{t("label_no_pain")}</Radio>
              <Radio value="light-pain">{t("label_light_pain")}</Radio>
              <Radio value="moderate-pain">{t("label_moderate_pain")}</Radio>
              <Radio value="severe-pain">{t("label_severe_pain")}</Radio>
            </Radio.Group>
          </Form.Item>

          {programPainLevel && programPainLevel !== "no-pain" && (
            <div id="frequency-of-pain-additional">
              <Form.Item
                label={t("label_frequency_of_pain")}
                name="frequency-of-pain"
              >
                <Radio.Group
                >
                  <Radio value="sometimes">{t("label_sometimes")}</Radio>
                  <Radio value="often">{t("label_often")}</Radio>
                  <Radio value="always">{t("label_always")}</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                label={t("label_origin_of_pain")}
                name="origin-of-pain"
              >
                <Radio.Group
                >
                  <Space direction="vertical">
                    <Radio value="effort-linked">{t("label_effort_linked")}</Radio>
                    <Radio value="arthritis">{t("label_arthritis")}</Radio>
                    <Radio value="tendinopathy">{t("label_tendinopathy")}</Radio>
                    <Radio value="algic">{t("label_algic")}</Radio>
                    <Radio value="secondary-effect">{t("label_secondary_effect")}</Radio>
                    <Radio value="other">{t("label_other")}</Radio>
                  </Space>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                label={t("label_modification_to_address")}
                name="modification-to-address"
              >
                <Checkbox.Group
                >
                  <Space direction="vertical">
                    <Checkbox value="none">{t("label_none")}</Checkbox>
                    <Checkbox value="perform-easy-only">{t("label_perform_easy_only")}</Checkbox>
                    <Checkbox value="decrease-repetitions">{t("label_decrease_repetitions")}</Checkbox>
                    <Checkbox value="increase-rest">{t("label_increase_rest")}</Checkbox>
                    <Checkbox value="decrease-sets">{t("label_decrease_sets")}</Checkbox>
                    <Checkbox value="remove-one-exercise">{t("label_remove_one_exercise")}</Checkbox>
                    <Checkbox value="remove-two-exercises">{t("label_remove_two_exercises")}</Checkbox>
                  </Space>
                </Checkbox.Group>
              </Form.Item>
              <hr></hr>
              <Typography.Paragraph style={{ whiteSpace:"pre-wrap", lineHeight:"1.3" }}>
                {t("label_pain_advice")}
              </Typography.Paragraph>
              <hr></hr>
            </div>
          )}

          {inputYesOrNo(
            "label_feeling_of_safety",
            setSessionSafetyFeel,
            sessionSafetyFeel,
            "feeling-of-safety"
          )}

          {sessionSafetyFeel && sessionSafetyFeel === "no" && (
            <div id="session-safety-feel-additional">
              <Form.Item
                label={t("label_if_not_why")}
                name="safety-feel-why"
              >
                {inputTextArea()}
              </Form.Item>
              <Form.Item
                label={t("label_not_safe_correction")}
                name="safety-feel-correction"
                extra={t("hint_not_safe_correction")}
              >
                {inputTextArea()}
              </Form.Item>
            </div>
          )}

          <Typography.Title level={3}>
            {t("section_walking")}
          </Typography.Title>

          {inputYesOrNo(
            "label_has_walked_past_weeks",
            setHasWalkedPastWeeks,
            hasWalkedPastWeeks,
            "has-walked-past-weeks"
          )}

          {hasWalkedPastWeeks && hasWalkedPastWeeks === "yes" && (
            <div id="has-walked-additional">
              <Form.Item
                label={t("label_has_walked_frequency")}
                name="has-walked-frequency"
              >
                <Radio.Group
                >
                  <Space direction="vertical">
                    {inputsWeeklyFrequency()}
                  </Space>
                </Radio.Group>
              </Form.Item>

              {inputNumber(
                "label_has_walked_average",
                "has-walked-average",
                1,
                "hint_average_minutes",
                "input_suffix_minutes"
              )}

              {inputsPracticeWay(
                "label_prescription_practice_way",
                "walking-prescription-practice-way"
              )}
            </div>
          )}

          {inputYesOrNo(
            "label_has_fallen_past_weeks",
            setHasFallenPastWeeks,
            hasFallenPastWeeks,
            "has-fallen-past-weeks"
          )}

          {hasFallenPastWeeks && hasFallenPastWeeks === "yes" && (
            <div id="has-fallen-additional">
              {inputNumber(
                "label_if_yes_how_much",
                "times-fallen-count",
                1
              )}

              <Form.Item
                label={t("label_if_yes_circumstances")}
                name="fall-circumstances"
              >
                {inputTextArea()}
              </Form.Item>

              <hr></hr>
              <Typography.Paragraph style={{ whiteSpace:"pre-wrap", lineHeight:"1.3" }}>
                {t("label_fall_advice")}
              </Typography.Paragraph>
              <hr></hr>
            </div>  
          )}

          {inputYesOrNo(
            "label_prescribed_ap_motivation",
            setIsMotivatedByAP,
            isMotivatedByAP,
            "prescriped-ap-motivation"
          )}

          {isMotivatedByAP && isMotivatedByAP === "no" && (
            <Form.Item
              label={t("label_if_not_why")}
              name="not-motivated-reason"
            >
              {inputTextArea()}
            </Form.Item>
          )}

          <Typography.Title level={3}>
            {t("section_other_physical_activity")}
          </Typography.Title>

          {inputYesOrNo(
            "label_integrated_other_activity",
            setOtherActivityIntegration,
            otherActivityIntegration,
            "other-activity-integration"
          )}

          {otherActivityIntegration && otherActivityIntegration === "yes" && (
            inputsOtherActivity(1)
          )}
          {otherActivityIntegration === "yes" && anotherActivityIntegration === "yes" && (
            inputsOtherActivity(2)
          )}
          {otherActivityIntegration === "yes" && anotherActivityIntegration === "yes" 
            && yetAnotherActivityIntegration === "yes" && (
            inputsOtherActivity(3)
          )}
          
          <Typography.Title level={3}>
            {t("section_medical_consultation")}
          </Typography.Title>

          {inputYesOrNo(
            "label_has_seen_doctor",
            setHasSeenDoctor,
            hasSeenDoctor,
            "has-seen-doctor"
          )}

          {hasSeenDoctor && hasSeenDoctor === "yes" && (
            inputsConsultation("doctor")
          )}

          {inputYesOrNo(
            "label_hospital_day_surgery",
            setHasBeenToHospital,
            hasBeenToHospital,
            "hospital-day-surgery"
          )}

          {hasBeenToHospital && hasBeenToHospital === "yes" && (
            inputsConsultation("hospital")
          )}

          {inputYesOrNo(
            "label_has_seen_other_professional",
            setHasConsultedSpecialist,
            hasConsultedSpecialist,
            "has-seen-other-professional"
          )}

          {hasConsultedSpecialist && hasConsultedSpecialist === "yes" && (
            inputsConsultation("specialist")
          )}

          <Form.Item>
            <Space>
              <Button
                type="default"
                icon={<CloseOutlined />}
                onClick={() => { window.location.href = "/evaluations"; }}
              >
                {t("button_cancel")}
              </Button>
              <Button type="primary" htmlType="submit" icon={<SendOutlined />}>
                {t("button_submit_followup")}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Col>
    </Row>
  )
}
